import { type FC, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Avatar,
  Card,
  CardHeader,
  Divider,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import LiveTvOutlinedIcon from "@mui/icons-material/LiveTvOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useTranslation } from "react-i18next";
import { initials } from "@/shared/components/SupportThread";
import { touchSummary } from "@/shared/components/AttributionTouch";
import { interpolationFormat } from "@/partner/shared/lib";
import { formatAmountObject } from "@/partner/features/products";
import {
  REQUEST_TYPE_LABEL_KEY,
  SupportRequestStatusChip,
} from "@/partner/entities/assistant";
import type { SupportRequestQueryType } from "@/types/graphql/partner.generated";
import { displayName } from "../model/thread";
import { viewerHref, viewerPaymentsHref } from "../model/links";

type Request = NonNullable<SupportRequestQueryType["supportRequest"]>;

const NEEDS_REPLY = new Set(["OPEN", "VIEWER_REPLIED"]);

/** Строка сведений: иконка, подпись мелко, значение обычным текстом. */
const InfoRow: FC<{ icon: ReactNode; label: string; children: ReactNode }> = ({
  icon,
  label,
  children,
}) => (
  <ListItem sx={{ alignItems: "flex-start", py: 1 }}>
    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>{icon}</ListItemIcon>
    <ListItemText
      primary={label}
      secondary={children}
      slotProps={{
        primary: { variant: "caption", color: "text.secondary" },
        secondary: {
          component: "div",
          variant: "body2",
          color: "text.primary",
          sx: { mt: 0.25, minWidth: 0, wordBreak: "break-word" },
        },
      }}
    />
  </ListItem>
);

/** Сведения об обращении: справа от ленты на md и шире, над ней уже. */
export const SupportRequestInfoPanel: FC<{ request: Request }> = ({
  request,
}) => {
  const { t, i18n } = useTranslation();
  const empty = t("common.emptyDash");
  const viewerEmail = request.user?.email ?? "";
  const viewerName = displayName(request.user);
  const assigneeName = displayName(request.assignee);
  const waiting =
    NEEDS_REPLY.has(`${request.status}`) && Boolean(request.waitingSince);
  const letters = initials(viewerName);

  return (
    <Card data-block="info">
      <CardHeader
        title={t("requests.card.infoTitle")}
        slotProps={{ title: { variant: "subtitle1", fontWeight: 600 } }}
        sx={{ px: 2, pt: 2, pb: 1 }}
      />
      <List dense disablePadding sx={{ pb: 1 }}>
        <InfoRow
          icon={<FlagOutlinedIcon fontSize="small" />}
          label={t("requests.card.status")}
        >
          <Stack spacing={0.5} alignItems="flex-start">
            <SupportRequestStatusChip status={request.status} />
            {waiting && (
              <Typography variant="caption" color="warning.main">
                {t("requests.card.waitingSince", {
                  date: request.waitingSince,
                })}
              </Typography>
            )}
          </Stack>
        </InfoRow>
        <Divider component="li" sx={{ my: 0.5 }} />
        <ListItem sx={{ alignItems: "flex-start", py: 1 }}>
          <ListItemAvatar sx={{ minWidth: 52 }}>
            <Avatar sx={{ width: 36, height: 36, fontSize: 15 }}>
              {letters || <PersonOutlineIcon fontSize="small" />}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={viewerName ?? empty}
            secondary={
              <Stack spacing={0.5} alignItems="flex-start">
                {viewerEmail && viewerEmail !== viewerName && (
                  <Tooltip title={viewerEmail}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ maxWidth: 1 }}
                    >
                      {viewerEmail}
                    </Typography>
                  </Tooltip>
                )}
                {request.user?.id && (
                  <Link
                    component={RouterLink}
                    to={viewerHref(request.user.id)}
                    variant="body2"
                    color="secondary"
                  >
                    {t("requests.card.openViewer")}
                  </Link>
                )}
              </Stack>
            }
            slotProps={{
              primary: { variant: "subtitle2", noWrap: true },
              secondary: { component: "div", sx: { minWidth: 0 } },
            }}
          />
        </ListItem>
        <Divider component="li" sx={{ my: 0.5 }} />
        <InfoRow
          icon={<LiveTvOutlinedIcon fontSize="small" />}
          label={t("requests.card.platform")}
        >
          {request.platform?.title ?? empty}
        </InfoRow>
        <InfoRow
          icon={<CategoryOutlinedIcon fontSize="small" />}
          label={t("requests.card.type")}
        >
          {t(REQUEST_TYPE_LABEL_KEY[request.type])}
        </InfoRow>
        {request.payment && (
          <InfoRow
            icon={<CreditCardOutlinedIcon fontSize="small" />}
            label={t("requests.card.payment")}
          >
            <Stack spacing={0.25} alignItems="flex-start">
              <span>{formatAmountObject(request.payment.amount, empty)}</span>
              {request.payment.product && (
                <Typography variant="caption" color="text.secondary">
                  {request.payment.product.title}
                </Typography>
              )}
              {viewerEmail && (
                <Link
                  component={RouterLink}
                  to={viewerPaymentsHref(viewerEmail)}
                  variant="body2"
                  color="secondary"
                >
                  {t("requests.card.openPayment")}
                </Link>
              )}
            </Stack>
          </InfoRow>
        )}
        <InfoRow
          icon={<CalendarTodayOutlinedIcon fontSize="small" />}
          label={t("requests.card.created")}
        >
          {interpolationFormat(request.createdAt, "dateTime", i18n.language)}
        </InfoRow>
        <InfoRow
          icon={<CampaignOutlinedIcon fontSize="small" />}
          label={t("requests.card.came")}
        >
          {touchSummary(request.utm)}
        </InfoRow>
        <InfoRow
          icon={<AssignmentIndOutlinedIcon fontSize="small" />}
          label={t("requests.card.assignee")}
        >
          {assigneeName ?? (
            <Typography variant="body2" color="text.secondary">
              {t("requests.card.noAssignee")}
            </Typography>
          )}
        </InfoRow>
      </List>
    </Card>
  );
};
